<template>
  <div class="kv-ve-border">
    <div class="kv-ve-border__top">
      <label class="kv-ve-border__label">{{ label }}</label>
      <label class="kv-ve-border__toggle">
        <input type="checkbox" class="kv-ve-border__toggle-input" :checked="safe.enabled" @change="onToggle" />
        <span class="kv-ve-border__toggle-pill" aria-hidden="true"></span>
        <span class="kv-ve-border__toggle-text">{{ safe.enabled ? enabledLabel : disabledLabel }}</span>
      </label>
    </div>

    <div v-if="safe.enabled" class="kv-ve-border__body">
      <div v-if="!hideSides" class="kv-ve-border__row">
        <div class="kv-ve-border__row-title">{{ sidesLabel }}</div>
        <div class="kv-ve-border__sides" role="group" :aria-label="sidesLabel">
          <button
            type="button"
            class="kv-ve-border__side kv-ve-border__side--top"
            :class="{ active: safe.sides.top }"
            :aria-pressed="String(!!safe.sides.top)"
            @click="toggleSide('top')"
          >
            <span class="kv-ve-border__side-icon kv-ve-border__side-icon--top"></span>
          </button>
          <button
            type="button"
            class="kv-ve-border__side kv-ve-border__side--right"
            :class="{ active: safe.sides.right }"
            :aria-pressed="String(!!safe.sides.right)"
            @click="toggleSide('right')"
          >
            <span class="kv-ve-border__side-icon kv-ve-border__side-icon--right"></span>
          </button>
          <button
            type="button"
            class="kv-ve-border__side kv-ve-border__side--bottom"
            :class="{ active: safe.sides.bottom }"
            :aria-pressed="String(!!safe.sides.bottom)"
            @click="toggleSide('bottom')"
          >
            <span class="kv-ve-border__side-icon kv-ve-border__side-icon--bottom"></span>
          </button>
          <button
            type="button"
            class="kv-ve-border__side kv-ve-border__side--left"
            :class="{ active: safe.sides.left }"
            :aria-pressed="String(!!safe.sides.left)"
            @click="toggleSide('left')"
          >
            <span class="kv-ve-border__side-icon kv-ve-border__side-icon--left"></span>
          </button>
          <div class="kv-ve-border__sides-box" aria-hidden="true">{{ t('visual_editor-border') }}</div>
        </div>
      </div>

      <div class="kv-ve-border__row">
        <div class="kv-ve-border__row-title">{{ widthLabel }}</div>
        <input
          class="form-control kv-ve-border__width"
          type="number"
          :min="minWidth"
          :max="maxWidth"
          :step="stepWidth"
          :value="safe.width"
          @input="setWidth($event.target.value)"
        />
      </div>

      <div class="kv-ve-border__row">
        <div class="kv-ve-border__row-title">{{ styleLabel }}</div>
        <div class="kv-ve-border__styles" role="group" :aria-label="styleLabel">
          <button
            v-for="opt in styleOptions"
            :key="opt.value"
            type="button"
            class="kv-ve-border__style"
            :class="{ active: safe.style === opt.value }"
            :aria-pressed="String(safe.style === opt.value)"
            @click="setStyle(opt.value)"
          >
            <span class="kv-ve-border__style-line" :style="{ borderTopStyle: opt.value }"></span>
          </button>
        </div>
      </div>

      <div class="kv-ve-border__row">
        <div class="kv-ve-border__row-title">{{ colorLabel }}</div>
        <VeColorPicker :value="safe.color" @input="setColor" />
      </div>
    </div>
  </div>
</template>

<script>
import VeColorPicker from "./VeColorPicker.vue";

export default {
  name: "VeBorderSettings",
  components: { VeColorPicker },
  model: { prop: "value", event: "input" },
  props: {
    value: { type: Object, default: () => ({}) },
    minWidth: { type: Number, default: 1 },
    maxWidth: { type: Number, default: 20 },
    stepWidth: { type: Number, default: 1 },
    hideSides: { type: Boolean, default: false },
  },
  data() {
    return {
      label: this.$_("visualEditor-emailEditor-borderSettings-label"),
      colorLabel: this.$_("visualEditor-emailEditor-borderSettings-color"),
      enabledLabel: this.$_("visualEditor-emailEditor-borderSettings-enabled"),
      disabledLabel: this.$_("visualEditor-emailEditor-borderSettings-disabled"),
      sidesLabel: this.$_("visualEditor-emailEditor-borderSettings-sides"),
      widthLabel: this.$_("visualEditor-emailEditor-borderSettings-width"),
      styleLabel: this.$_("visualEditor-emailEditor-borderSettings-style"),
    };
  },
  computed: {
    safe() {
      const v = this.value || {};
      const enabled = Boolean(v.enabled);
      const styleRaw = String(v.style || "solid").toLowerCase();
      const style = styleRaw === "dashed" || styleRaw === "dotted" ? styleRaw : "solid";
      const color = typeof v.color === "string" && v.color ? v.color : "#e5e7eb";
      const wRaw = Number(v.width ?? 1);
      const width = Number.isFinite(wRaw)
        ? Math.min(this.maxWidth, Math.max(this.minWidth, Math.round(wRaw)))
        : this.minWidth;
      const sides = v.sides || {};
      const normSides = {
        top: sides.top !== false,
        right: sides.right !== false,
        bottom: sides.bottom !== false,
        left: sides.left !== false,
      };
      return { enabled, style, color, width, sides: normSides };
    },
    styleOptions() {
      return [
        { value: "solid", label: this.t("visual_editor-solid") },
        { value: "dashed", label: this.t("visual_editor-dashed") },
        { value: "dotted", label: this.t("visual_editor-dotted") },
      ];
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    emitNext(patch) {
      this.$emit("input", { ...this.safe, ...(patch || {}) });
    },
    onToggle(e) {
      const enabled = Boolean(e?.target?.checked);
      if (enabled) {
        // When enabling, default to all sides on.
        this.emitNext({
          enabled: true,
          width: this.safe.width || this.minWidth,
          sides: { top: true, right: true, bottom: true, left: true },
        });
      } else {
        this.emitNext({ enabled: false });
      }
    },
    setWidth(raw) {
      const n = Number(raw);
      const next = Number.isFinite(n)
        ? Math.min(this.maxWidth, Math.max(this.minWidth, Math.round(n)))
        : this.safe.width;
      this.emitNext({ width: next });
    },
    toggleSide(side) {
      const nextSides = { ...this.safe.sides, [side]: !this.safe.sides[side] };
      this.emitNext({ sides: nextSides });
    },
    setStyle(style) {
      const v = String(style || "").toLowerCase();
      const safe = v === "dashed" || v === "dotted" ? v : "solid";
      this.emitNext({ style: safe });
    },
    setColor(raw) {
      this.emitNext({ color: raw || "#e5e7eb" });
    },
  },
};
</script>

<style scoped lang="less">
.kv-ve-border__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.kv-ve-border__label {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #344054;
}

.kv-ve-border__toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}
.kv-ve-border__toggle-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.kv-ve-border__toggle-pill {
  width: 42px;
  height: 24px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f2f4f7;
  position: relative;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.kv-ve-border__toggle-pill:before {
  content: "";
  position: absolute;
  top: 50%;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  transform: translateY(-50%);
  transition: transform 0.15s ease;
}
.kv-ve-border__toggle-input:checked + .kv-ve-border__toggle-pill {
  background: rgba(42, 170, 76, 0.18);
  border-color: rgba(42, 170, 76, 0.45);
}
.kv-ve-border__toggle-input:checked + .kv-ve-border__toggle-pill:before {
  transform: translate(18px, -50%);
  border-color: rgba(42, 170, 76, 0.55);
}
.kv-ve-border__toggle-text {
  font-size: 12px;
  font-weight: 600;
  color: #667085;
}

.kv-ve-border__body {
  margin-top: 10px;
  display: grid;
  gap: 12px;
}

.kv-ve-border .kv-ve-border__row .kv-ve-border__row-title {
  font-size: 10px;
  font-weight: 400;
  color: #959595;
  margin-bottom: 8px;
}

.kv-ve-border__sides {
  display: grid;
  grid-template-columns: 44px 1fr 44px;
  grid-template-rows: 28px 64px 28px;
  grid-template-areas:
    ". top ."
    "left box right"
    ". bottom .";
  align-items: center;
  gap: 8px;
}
.kv-ve-border__sides-box {
  grid-area: box;
  height: 64px;
  border: 1px dashed #d0d5dd;
  border-radius: 14px;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  color: #667085;
}
.kv-ve-border__side {
  all: unset;
  cursor: pointer;
  width: 44px;
  height: 28px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kv-ve-border__side-icon {
  width: 16px;
  height: 16px;
  border: 2px dashed #98a2b3;
  background: transparent;
  position: relative;
}
.kv-ve-border__side-icon--top {
  border-top: 2px solid #98a2b3;
  border-left: 0;
  border-right: 0;
  border-bottom: 0;
}
.kv-ve-border__side-icon--right {
  border-right: 2px solid #98a2b3;
  border-top: 0;
  border-left: 0;
  border-bottom: 0;
}
.kv-ve-border__side-icon--bottom {
  border-bottom: 2px solid #98a2b3;
  border-top: 0;
  border-left: 0;
  border-right: 0;
}
.kv-ve-border__side-icon--left {
  border-left: 2px solid #98a2b3;
  border-top: 0;
  border-right: 0;
  border-bottom: 0;
}

.kv-ve-border__side.active .kv-ve-border__side-icon {
  border-color: rgba(42, 170, 76, 0.85);
}

.kv-ve-border__side.active {
  border-color: rgba(42, 170, 76, 0.85);  
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.16);
  background: rgba(42, 170, 76, 0.06);
}

.kv-ve-border__side--top {
  grid-area: top;
  justify-self: center;
}
.kv-ve-border__side--right {
  grid-area: right;
  justify-self: end;
}
.kv-ve-border__side--bottom {
  grid-area: bottom;
  justify-self: center;
}
.kv-ve-border__side--left {
  grid-area: left;
  justify-self: start;
}

.kv-ve-border__styles {
  display: flex;
  align-items: center;
  justify-content: flext-start;
  gap: 10px;
}

.kv-ve-border__style {
  all: unset;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px;
  background: #fff;
  display: flex; /* Changed to flex */
  align-items: center;
  justify-content: center;
}
.kv-ve-border__style:hover {
  border-color: rgba(42, 170, 76, 0.55);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.12);
}
.kv-ve-border__style.active {
  border-color: rgba(42, 170, 76, 0.85);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.16);
}
.kv-ve-border__style-line {
  width: 100%;
  max-width: 70px;
  width: 32px;
  border-top-width: 2px;
  border-top-color: #344054;
  border-top-style: solid;
  display: block;
}

.kv-ve-border__style.active .kv-ve-border__style-line {
  border-top-color: rgba(42, 170, 76, 0.85);
}
</style>


    